// overseerGate.ts — m31 P2 (O2): who may be recorded by Overseer (C813 product analytics).
//
// PURE and import.meta-free so jest can load it (see utils/promoLabel.ts for why). The caller reads the page and the
// auth state and passes them in; nothing here touches a global.
//
// The bot / QA rules are NOT restated here: they are `isBlockedTraffic` in services/analytics.ts, the same rule set
// that already keeps this traffic out of GTM, so the two can never drift apart.
//
// Allowed: signed-out visitors, customers (client) and partners.
// Not allowed: bots and QA traffic, every staff role, impersonated ("Login as Customer") sessions, test accounts, and
// the internal /admin and /sales consoles (the retired session recorder was also paused on /admin).
import type { UserRole } from '@siteforge/shared';
import { isBlockedTraffic } from '../services/analytics';

/**
 * Exhaustive on purpose: adding a role to UserRole fails the type check until someone decides whether that role is
 * recorded. An unknown role at runtime (a stale token) is NOT recorded — staff privacy is the failure to avoid.
 */
const ROLE_IS_RECORDED: Record<UserRole, boolean> = {
  client: true,
  partner: true,
  admin: false,
  superadmin: false,
  sub_admin: false,
  sales: false,
  va: false,
  support: false,
};

/** The slice of the signed-in user (JwtPayload) the gate reads. */
export interface OverseerGateUser {
  role: UserRole;
  email?: string;
  isTestAccount?: boolean;
  impersonatedBy?: string;
}

export interface OverseerGateInput {
  /** null = signed out. */
  user: OverseerGateUser | null;
  /** useAuth().isImpersonating. `user.impersonatedBy` is honoured too. */
  impersonating?: boolean;
  /** An email to check against the QA address rule, in addition to `user.email`. */
  email?: string;
  pathname?: string;
  /** location.search */
  search?: string;
  /** document.cookie */
  cookies?: string;
  userAgent?: string;
  /** sessionStorage `sf_qa_session === '1'`. A `sf_qa_session=1` cookie is honoured as well. */
  qaSession?: boolean;
}

const QA_COOKIE_RE = /(?:^|;\s*)sf_qa_session=1(?:;|$)/;

/** `/admin`, `/admin/...`, `/sales`, `/sales/...` — never `/administrator` or a brand slug that merely starts alike. */
function isInternalPath(pathname: string | undefined): boolean {
  if (!pathname) return false;
  return /^\/(?:admin|sales)(?:\/|$)/i.test(pathname);
}

export function shouldInitOverseer(input: OverseerGateInput): boolean {
  const { user } = input;

  const traffic = {
    userAgent: input.userAgent,
    search: input.search,
    qaSession: !!input.qaSession || QA_COOKIE_RE.test(input.cookies || ''),
  };
  // Both addresses are checked (they are normally the same one); the page-level rules ride along on each pass.
  if ([input.email, user?.email].some((email) => isBlockedTraffic({ ...traffic, email }))) return false;

  if (isInternalPath(input.pathname)) return false;
  if (input.impersonating || user?.impersonatedBy) return false;
  if (!user) return true;
  if (user.isTestAccount === true) return false;
  return ROLE_IS_RECORDED[user.role] === true;
}
